import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Slider, Stack, Collapse} from 'views/components';
import {Translate, translate, CardSelector, Map} from 'views/module';
import {DirectionsCar, DirectionsTransit, DirectionsWalk, DirectionsBike, Info} from 'views/icons';

const TravelPreferencesSection = (props) => {
  const {onTravelPreferencesChange, travelPreferences = {}, setValidity} = props;

  const MINS_TO_HOURS = 1 / 60;
  const KM_TO_M = 1000;
  const travelSpeedsKmh = {
    car: 40,
    transit: 15,
    cycling: 11,
    walking: 4,
  };


  // State to store user's GPS coordinates
  const [userLocation, setUserLocation] = React.useState({lat: 44.556046282906564, lng: -77.68675655638565});
  const [locationClicked, setLocationClicked] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const [mapKey, setMapKey] = React.useState(Date.now());

  // Fetch GPS coordinates on first load
  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
          (position) => {
            setMapKey(Date.now());
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            setUserLocation({
              lat: 44.556046282906564,
              lng: -77.68675655638565,
            });
          },
      );
    } else {
      console.info('Geolocation is not supported by this browser.');
    }
  }, []);

  // Transport mode options with icons
  const transportModes = [
    {
      id: 'car',
      label: translate('caregiverScreening.TravelPreferences.TransportModes.Car.Label'),
      icon: DirectionsCar,
      description: translate('caregiverScreening.TravelPreferences.TransportModes.Car.Description'),
    },
    {
      id: 'transit',
      label: translate('caregiverScreening.TravelPreferences.TransportModes.Transit.Label'),
      icon: DirectionsTransit,
      description: translate('caregiverScreening.TravelPreferences.TransportModes.Transit.Description'),
    },
    {
      id: 'cycling',
      label: translate('caregiverScreening.TravelPreferences.TransportModes.Cycling.Label'),
      icon: DirectionsBike,
      description: translate('caregiverScreening.TravelPreferences.TransportModes.Cycling.Description'),
    },
    {
      id: 'walking',
      label: translate('caregiverScreening.TravelPreferences.TransportModes.Walking.Label'),
      icon: DirectionsWalk,
      description: translate('caregiverScreening.TravelPreferences.TransportModes.Walking.Description'),
    },
  ];

  // Get current selections
  const selectedMode = travelPreferences.transportMode || null; // Single mode instead of array
  const maxTravelTime = travelPreferences.maxTravelTime || 30; // Default to 30 minutes

  const estimateRadius = selectedMode ? maxTravelTime * MINS_TO_HOURS * travelSpeedsKmh[selectedMode] * KM_TO_M : 0;

  // Update validity when travel preferences change
  React.useEffect(() => {
    if (setValidity) {
      const isValid = Boolean(
          travelPreferences.transportMode &&
          travelPreferences.maxTravelTime !== undefined,
      );
      setValidity(isValid);
    }
  }, [travelPreferences, setValidity]);

  // Handle transport mode selection
  const handleModeChange = (modeId) => {
    const updatedPreferences = {
      ...travelPreferences,
      transportMode: modeId, // Set single mode
    };

    if (onTravelPreferencesChange) {
      onTravelPreferencesChange(updatedPreferences);
    }
  };

  // Handle travel time change
  const handleTravelTimeChange = (event, newValue) => {
    const updatedPreferences = {
      ...travelPreferences,
      maxTravelTime: newValue,
    };

    if (onTravelPreferencesChange) {
      onTravelPreferencesChange(updatedPreferences);
    }
  };

  const handleMapClick = (latlng) => {
    setUserLocation(latlng);
    setLocationClicked(true);
  };

  // Format time for display
  const formatTravelTime = (minutes) => {
    if (minutes === 60) {
      return translate('caregiverScreening.TravelPreferences.TimeFormats.OneHour');
    }
    return translate('caregiverScreening.TravelPreferences.TimeFormats.Minutes', {minutes});
  };

  // Render icon component for selected mode
  const renderSelectedModeIcon = () => {
    const selectedModeData = transportModes.find((mode) => mode.id === selectedMode);
    if (!selectedModeData) return null;

    const IconComponent = selectedModeData.icon;
    return <IconComponent sx={{fontSize: 20, color: 'text.primary'}} />;
  };

  // Marks for the slider
  const travelTimeMarks = [
    {
      value: 5,
      label: translate('caregiverScreening.TravelPreferences.TimeMarks.FiveMin'),
    },
    {
      value: 30,
      label: translate('caregiverScreening.TravelPreferences.TimeMarks.ThirtyMin'),
    },
    {
      value: 60,
      label: translate('caregiverScreening.TravelPreferences.TimeMarks.OneHour'),
    },
  ];

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.TravelPreferences.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.TravelPreferences.Subtitle'/>
      </Typography>

      {/* Transport Mode Selection */}
      <CardSelector
        title={translate('caregiverScreening.TravelPreferences.TransportationMethodTitle')}
        description={translate('caregiverScreening.TravelPreferences.TransportationMethodDescription')}
        options={transportModes}
        selectedValue={selectedMode}
        onSelectionChange={handleModeChange}
        gridProps={{size: {xs: 6, sm: 3}}}
      />

      {/* Maximum Travel Time Slider */}
      <Box sx={{backgroundColor: 'action.hover', p: 3, borderRadius: 1, mt: 4}}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant="h6" >
            <Translate expression='caregiverScreening.TravelPreferences.MaxTravelTimeTitle'/>
          </Typography>
          <Typography variant="h6" color="primary.main" fontWeight={600}>
            {formatTravelTime(maxTravelTime)}
          </Typography>
        </Box>

        <Box sx={{px: 2}}>
          <Stack>
            <Slider
              value={maxTravelTime}
              onChange={handleTravelTimeChange}
              step={5}
              marks={travelTimeMarks}
              min={5}
              max={60}
              valueLabelDisplay="off"
              valueLabelFormat={formatTravelTime}
              sx={{
                '& .MuiSlider-mark': {
                  'height': 8,
                  'width': 2,
                  'backgroundColor': 'primary.light',
                },
                '& .MuiSlider-markActive': {
                  'backgroundColor': 'primary.main',
                },
                '& .MuiSlider-markLabel': {
                  'fontSize': '0.75rem',
                  'color': 'text.secondary',
                  'top': 28,
                },
                '& .MuiSlider-thumb': {
                  'height': 20,
                  'width': 20,
                  'backgroundColor': '#fff',
                  'border': '2px solid currentColor',
                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                    'boxShadow': 'inherit',
                  },
                  '&::before': {
                    'display': 'none',
                  },
                },
                '& .MuiSlider-valueLabel': {
                  'lineHeight': 1.2,
                  'fontSize': 12,
                  'background': 'unset',
                  'padding': 0,
                  'width': 32,
                  'height': 32,
                  'borderRadius': '50% 50% 50% 0',
                  'backgroundColor': 'primary.main',
                  'transformOrigin': 'bottom left',
                  'transform': 'translate(50%, -100%) rotate(-45deg) scale(0)',
                  '&::before': {'display': 'none'},
                  '&.MuiSlider-valueLabelOpen': {
                    'transform': 'translate(50%, -100%) rotate(-45deg) scale(1)',
                  },
                  '& > *': {
                    'transform': 'rotate(45deg)',
                  },
                },
              }}
            />
          </Stack>
        </Box>

        {/* Map Estimate */}
        <Typography variant="subtitle2" color="text.secondary" align="center" sx={{mt: 1, fontWeight: 600, display: 'block'}}>
          <Translate expression="caregiverScreening.TravelPreferences.EstimateTitle" defaultValue="Travel Range Estimate" />
        </Typography>
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
          {
            userLocation && (
              <Box
                sx={{position: 'relative', width: '100%', height: '300px', overflow: 'visible', borderRadius: 1}}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                <Map
                  key={mapKey}
                  width='100%'
                  height='300px'
                  coordinates={[userLocation.lat, userLocation.lng]}
                  radius={locationClicked ? estimateRadius : 0}
                  zoomLevel={7}
                  onLocationClick={handleMapClick}
                  showMarker={locationClicked}
                />
                {
                  !locationClicked && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: !isHovering ? 'rgba(0,0,0,0.4)' : 'transparent',
                        color: !isHovering ? '#fff' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1200,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '1.2rem',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Translate expression="caregiverScreening.TravelPreferences.MapClickInstruction" />
                    </Box>
                  )
                }
              </Box>
            )
          }
        </Box>
        {/* Estimate disclaimer blurb */}
        <Typography variant="caption" color="text.secondary" align="center" sx={{mt: 0.5, display: 'block'}}>
          <Translate expression="caregiverScreening.TravelPreferences.EstimateDisclaimer" />
        </Typography>

        {/* Important Info Warning Box */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'primary.light',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Info sx={{color: 'primary.main', mt: 0.2, fontSize: 20}} />
          <Box>
            <Typography variant="subtitle2" color="primary.dark" fontWeight={600} sx={{mb: 0.5}}>
              <Translate expression='caregiverScreening.TravelPreferences.ImportantInfo.Title'/>
            </Typography>
            <Typography variant="body2" color="primary.dark">
              <Translate expression='caregiverScreening.TravelPreferences.ImportantInfo.Message'/>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary */}
      <Collapse in={Boolean(selectedMode)} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{mb: 2}}>
            <Translate expression='caregiverScreening.TravelPreferences.Summary.Title'/>
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              {renderSelectedModeIcon()}
              <Typography variant="body1" color="text.primary">
                {transportModes.find((mode) => mode.id === selectedMode)?.label}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              |
            </Typography>
            <Typography variant="body1" color="primary.main" fontWeight={500}>
              <Translate expression='caregiverScreening.TravelPreferences.Summary.MaxLabel' substitute={{time: formatTravelTime(maxTravelTime)}}/>
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

TravelPreferencesSection.propTypes = {
  onTravelPreferencesChange: PropTypes.func,
  setValidity: PropTypes.func,
  travelPreferences: PropTypes.shape({
    transportMode: PropTypes.string, // Single mode instead of array
    maxTravelTime: PropTypes.number,
  }),
};

export default React.memo(TravelPreferencesSection);
